/**
 * Formats a number as European/Portuguese currency
 * Examples:
 *   17000 → "17.000,00€"
 *   17000.20 → "17.000,20€"
 *   1234.56 → "1.234,56€"
 */
export function formatEuro(amount: number): string {
  // Format with de-DE locale which uses . for thousands and , for decimals
  // Then replace the currency symbol with €
  const formatted = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  // Remove spaces and replace currency position (de-DE puts € before)
  return formatted.replace(/\s/g, '').replace('€', '') + '€';
}

/**
 * Formats a number as European/Portuguese currency without decimals
 * Examples:
 *   17000 → "17.000€"
 *   17000.20 → "17.000€"
 */
export function formatEuroNoDecimals(amount: number): string {
  const formatted = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  return formatted.replace(/\s/g, '').replace('€', '') + '€';
}

/**
 * Formats a number as currency with the given currency code
 * Examples:
 *   (1500, 'EUR') → "1.500,00€"
 *   (1500, 'USD') → "$1,500.00"
 */
export function formatCurrency(amount: number, currency: string = 'EUR'): string {
  if (currency === 'EUR') {
    return formatEuro(amount);
  }

  // For other currencies, use standard locale formatting
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  return formatted;
}
